import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP09 — "Teu Marketing Tá Caro ou Barato?"
 * Continuidade: EP08 prometeu "como saber se tá caro ou barato"
 * Este EP entrega: método LTV/CAC
 * CTA semeia: "terceiro número" (tempo até 1º pagamento) pro EP10
 *
 * BoardCopy: Halbert (voz pessoal) + Hormozi (especificidade) + Sugarman (curiosity)
 * Editing Style: DYNAMIC (Dark Premium)
 */

const ORANGE = "#FF6B35";
const GREEN = IDV.secondary;
const RED = IDV.error;
const AMBER = "#f5ad43";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";

const SZ = SAFE_ZONES.universal;

// Audio timing — synced to ElevenLabs
const SCENES = [
  {dur: 141, file: "audio/cafe_ep09_new/c01.mp3", dir: "right" as const},
  {dur: 370, file: "audio/cafe_ep09_new/c02.mp3", dir: "left" as const},
  {dur: 855, file: "audio/cafe_ep09_new/c03.mp3", dir: "up" as const},
  {dur: 462, file: "audio/cafe_ep09_new/c04.mp3", dir: "right" as const},
  {dur: 300, file: "audio/cafe_ep09_new/c05.mp3", dir: "up" as const},
];

export const CAFE_EP09_NEW_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── Comparison Card ──────────────────────────────────────────────────
const ComparisonCard: React.FC<{
  label: string; cac: string; ltv: string; ratio: string;
  color: string; delay: number; verdict: string;
}> = ({label, cac, ltv, ratio, color, delay, verdict}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateY(${interpolate(p, [0, 1], [30, 0], CLAMP)}px)`,
      padding: "24px 28px", borderRadius: 20, width: "100%",
      background: "rgba(255,255,255,0.04)",
      border: `1px solid ${color}33`,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>
        {label}
      </div>
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <div style={{textAlign: "center"}}>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>Custo</div>
          <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: WHITE, fontFamily: FONT_STACK}}>{cac}</div>
        </div>
        <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>vs</div>
        <div style={{textAlign: "center"}}>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>Retorno</div>
          <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: WHITE, fontFamily: FONT_STACK}}>{ltv}</div>
        </div>
        <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 900, color, fontFamily: FONT_STACK}}>= {ratio}</div>
      </div>
      <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK, textAlign: "center"}}>
        {verdict}
      </div>
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
    <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
    <Audio src={staticFile("sfx/whoosh/whoosh-01.mp3")} volume={0.4} />
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 8}}>
      <div style={{
        padding: "8px 24px", borderRadius: 999,
        background: "rgba(255,107,53,0.15)", border: "1px solid rgba(255,107,53,0.3)",
        fontSize: FONT_SCALE.label.min, fontWeight: 600, color: ORANGE, fontFamily: FONT_STACK,
      }}>
        EPISODIO 09
      </div>
      <GradientText text="CAFE COM TEU ROI" colors={[ORANGE, AMBER]} fontSize={FONT_SCALE.h1.min} springProfile="bouncy" />
    </div>
  </AbsoluteFill>
);

// ─── SCENE 2: PRINCÍPIO ──────────────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const titleP = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const subP = spring({frame: frame - 120, fps, config: SPRINGS.smooth});

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep09n">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <Sequence from={5} durationInFrames={10}>
        <Audio src={staticFile("sfx/impact/impact-02.mp3")} volume={0.5} />
      </Sequence>
      <div style={{
        opacity: titleP, fontSize: FONT_SCALE.h2.min, fontWeight: 700,
        color: WHITE, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
      }}>
        Marketing caro nao e{"\n"}o que custa muito.
      </div>
      <div style={{
        opacity: titleP,
        transform: `scale(${interpolate(titleP, [0, 1], [0.9, 1], CLAMP)})`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 800,
        color: ORANGE, fontFamily: FONT_STACK, textAlign: "center",
      }}>
        E o que custa mais{"\n"}do que traz.
      </div>
      <div style={{
        opacity: subP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", marginTop: 12,
      }}>
        Ontem: quanto custa trazer cliente.{"\n"}Hoje: se esse custo ta bom ou ta te quebrando.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: REFLEXÃO — comparação LTV/CAC ──────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const introP = spring({frame: frame - 5, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />
      <Sequence from={30} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-01.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={300} durationInFrames={10}>
        <Audio src={staticFile("sfx/notification/ping-01.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={550} durationInFrames={10}>
        <Audio src={staticFile("sfx/impact/impact-01.mp3")} volume={0.5} />
      </Sequence>
      <div style={{
        opacity: introP, fontSize: FONT_SCALE.body.min, fontWeight: 600,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center",
      }}>
        Dois cenarios. Mesma empresa.
      </div>
      <div style={{width: "88%", display: "flex", flexDirection: "column", gap: 16}}>
        <ComparisonCard
          label="CENARIO A" cac="R$200" ltv="R$500"
          ratio="2.5x" color={GREEN} delay={60}
          verdict="Gasta 1 pra ganhar 2,5. BARATO."
        />
        <ComparisonCard
          label="CENARIO B" cac="R$280" ltv="R$300"
          ratio="1.07x" color={RED} delay={300}
          verdict="Pagando pra trabalhar."
        />
      </div>
      <div style={{
        opacity: spring({frame: frame - 600, fps, config: SPRINGS.bouncy}),
        fontSize: FONT_SCALE.body.min, fontWeight: 700, color: AMBER,
        fontFamily: FONT_STACK, textAlign: "center", marginTop: 8,
      }}>
        Qual dos dois e o teu?
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: DESAFIO ────────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 80, fps, config: SPRINGS.smooth});
  const p3 = spring({frame: frame - 200, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <Sequence from={200} durationInFrames={10}>
        <Audio src={staticFile("sfx/cash/coin-01.mp3")} volume={0.5} />
      </Sequence>
      <div style={{
        opacity: p1, fontSize: FONT_SCALE.h2.min, fontWeight: 700,
        color: WHITE, fontFamily: FONT_STACK,
      }}>
        Desafio de hoje:
      </div>
      <div style={{
        opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "92%",
      }}>
        Pega os 5 ultimos clientes.{"\n"}Soma quanto cada um ja gastou.{"\n"}Divide pelo custo de aquisicao.
      </div>
      <div style={{
        opacity: p3,
        transform: `scale(${interpolate(p3, [0, 1], [0.85, 1], CLAMP)})`,
        padding: "20px 36px", borderRadius: 20,
        background: "rgba(255,107,53,0.12)", border: "1px solid rgba(255,107,53,0.25)",
        fontSize: FONT_SCALE.body.min, fontWeight: 800, color: ORANGE,
        fontFamily: FONT_STACK, textAlign: "center",
      }}>
        Se der menos que 3x,{"\n"}manda pro teu socio. Serio.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA ────────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 8, fps, config: SPRINGS.snappy});
  const saveP = spring({frame: frame - 60, fps, config: SPRINGS.bouncy});
  const seedP = spring({frame: frame - 140, fps, config: SPRINGS.smooth});
  const pulse = frame > 150 ? 1 + Math.sin((frame - 150) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
      <Sequence from={60} durationInFrames={10}>
        <Audio src={staticFile("sfx/success/success-01.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={140} durationInFrames={60}>
        <Audio src={staticFile("sfx/riser/riser-02.mp3")} volume={0.3} />
      </Sequence>
      {/* Save CTA */}
      <div style={{
        opacity: saveP,
        transform: `scale(${interpolate(saveP, [0, 1], [0.8, 1], CLAMP) * pulse})`,
        padding: "14px 32px", borderRadius: 999,
        background: "rgba(255,107,53,0.15)", border: "1px solid rgba(255,107,53,0.3)",
        fontSize: FONT_SCALE.body.min, fontWeight: 700, color: ORANGE,
        fontFamily: FONT_STACK,
      }}>
        SALVA ESSE CALCULO
      </div>
      {/* Profile */}
      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${ORANGE}`, boxShadow: `0 0 20px rgba(255,107,53,0.3)`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Cafe com teu ROI</div>
        </div>
      </div>
      {/* Seed for EP10 */}
      <div style={{
        opacity: seedP, fontSize: FONT_SCALE.body.min, fontWeight: 600,
        color: AMBER, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
      }}>
        Amanha: o terceiro numero.{"\n"}O que ninguem olha e que quebra{"\n"}mais empresa do que marketing ruim.
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp09New: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, #1a0a05, #000 70%)`}} />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
